// Compact deploy control for the Live Preview tab. The verbose deploy
// summary that used to occupy a full banner row now lives behind this
// three-dot menu (rendered next to "Add" in the list header), keeping
// the preview surface focused on the data grid.
//
// • A glowing red badge on the trigger draws attention whenever a deploy
//   or function-compile error is present.
// • Clicking the trigger opens the Deployment status modal, which is also
//   where the Redeploy button lives.
// • The Fullscreen toggle is intentionally NOT here — the list header
//   provides one (between "Add" and this menu).

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertTriangle, CheckCircle2, FileText, History, Loader2, MoreVertical, PencilRuler, RefreshCw,
  SlidersHorizontal, Snowflake, Trash2, X,
} from 'lucide-react'
import type { FunctionDeployResult, SyncReport } from '../types'
import ResetConfirmModal from './ResetConfirmModal'

interface Props {
  schema: string | null
  database: string | null
  sync: SyncReport | null
  loading: boolean
  error: string | null
  functions: FunctionDeployResult[]
  onRedeploy: () => void
  onOpenFailure: (f: FunctionDeployResult) => void
  // Opens the project-wide audit log; surfaced from this same 3-dot menu.
  onOpenAudit?: () => void
  // Opens the live-preview debug log viewer for this object.
  onOpenLog?: () => void
  // Opens Make changes (the screen itself, for everyone) — people who build
  // the app; listed above Customize.
  onMakeChanges?: () => void
  // Opens the per-tenant "Customize" panel (headings/order/protect/hidden).
  onCustomize?: () => void
  // Destructive "Reset data" (truncate preview rows). When both are provided a
  // menu item appears below "Deployment status", guarded by a type-the-name
  // confirm (the user must type ``resetConfirmName``). Omit to hide it.
  onReset?: () => void
  resetConfirmName?: string
  // "Freeze first column" — splits the table so the first column sits in its
  // own left scroll zone (the user can then drag the splitter to move it).
  // Surfaced just above "Reset data". Omit to hide it.
  onFreezeColumn?: () => void
}

export default function DeployMenu({
  schema, database, sync, loading, error, functions,
  onRedeploy, onOpenFailure, onOpenAudit, onOpenLog, onMakeChanges, onCustomize, onReset, resetConfirmName,
  onFreezeColumn,
}: Props) {
  const [open, setOpen] = useState(false)        // deployment status modal
  const [menuOpen, setMenuOpen] = useState(false)  // the 3-dot dropdown
  const [resetOpen, setResetOpen] = useState(false)  // reset type-to-confirm
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const MENU_WIDTH = 190

  const warnings = collectWarnings(sync)
  const fnErrors = functions.filter(f => f.status === 'error')
  const fnEmpty = functions.filter(f => f.status === 'empty')
  const fnOk = functions.filter(f => f.status === 'ok').length
  // Function-level SQL-quality findings (e.g. non-sargable predicates) — the
  // function deployed, but the index won't be used. Flatten to "name: finding".
  const fnWarnings = functions.flatMap(
    f => (f.warnings ?? []).map(w => `${f.name}: ${w}`),
  )

  const hasError = !!error || fnErrors.length > 0
  const hasWarning = !hasError && (warnings.length > 0 || fnWarnings.length > 0)

  // Close the modal on ESC.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // Anchor the dropdown under the trigger when it opens.
  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8))
    setMenuPos({ top: r.bottom + 4, left })
  }, [menuOpen])

  const trigger = (
    <button
      ref={triggerRef}
      className="btn btn-ghost btn-sm"
      onClick={() => setMenuOpen((v) => !v)}
      title={hasError ? 'More — deployment errors present' : 'More'}
      style={{ position: 'relative', padding: 6 }}
    >
      {loading ? (
        <Loader2 size={15} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
      ) : (
        <MoreVertical size={15} />
      )}
      {!loading && hasError && (
        <span
          aria-label="errors present"
          style={{
            position: 'absolute', top: 1, right: 1,
            width: 9, height: 9, borderRadius: '50%',
            background: '#ef4444',
            animation: 'glow-red 1.4s ease-in-out infinite',
          }}
        />
      )}
      {!loading && hasWarning && (
        <span
          aria-label="warnings present"
          style={{
            position: 'absolute', top: 1, right: 1,
            width: 9, height: 9, borderRadius: '50%',
            background: '#f59e0b',
          }}
        />
      )}
    </button>
  )

  // The 3-dot dropdown: Audit log + Deployment status (the deploy modal).
  const dropdown = menuOpen && createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9460 }}
      onClick={() => setMenuOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: menuPos?.top ?? -9999, left: menuPos?.left ?? -9999,
          width: MENU_WIDTH, background: 'var(--color-bg, #fff)', color: 'var(--color-text)',
          border: '1px solid var(--color-border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 4,
          display: 'flex', flexDirection: 'column',
        }}
      >
        {onMakeChanges && (
          <MenuItem onClick={() => { setMenuOpen(false); onMakeChanges() }}>
            <PencilRuler size={14} /> <span>Make changes</span>
          </MenuItem>
        )}
        {onCustomize && (
          <MenuItem onClick={() => { setMenuOpen(false); onCustomize() }}>
            <SlidersHorizontal size={14} /> <span>Customize</span>
          </MenuItem>
        )}
        {onOpenAudit && (
          <MenuItem onClick={() => { setMenuOpen(false); onOpenAudit() }}>
            <History size={14} /> <span>Audit log</span>
          </MenuItem>
        )}
        {onOpenLog && (
          <MenuItem onClick={() => { setMenuOpen(false); onOpenLog() }}>
            <FileText size={14} /> <span>Preview log</span>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setMenuOpen(false); setOpen(true) }}>
          {loading
            ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
            : hasError
              ? <AlertTriangle size={14} color="#ef4444" />
              : hasWarning
                ? <AlertTriangle size={14} color="#f59e0b" />
                : <CheckCircle2 size={14} color="#22c55e" />}
          <span>Deployment status</span>
        </MenuItem>
        {onFreezeColumn && (
          <MenuItem onClick={() => { setMenuOpen(false); onFreezeColumn() }}>
            <Snowflake size={14} /> <span>Freeze first column</span>
          </MenuItem>
        )}
        {onReset && resetConfirmName && (
          <MenuItem onClick={() => { setMenuOpen(false); setResetOpen(true) }}>
            <Trash2 size={14} color="#ef4444" />
            <span style={{ color: '#ef4444' }}>Reset data…</span>
          </MenuItem>
        )}
      </div>
    </div>,
    document.body,
  )

  const modal = (
    <div
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 9460,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          width: 'min(560px, 96vw)',
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <strong style={{ fontSize: '0.9625rem' }}>Deployment status</strong>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={onRedeploy} disabled={loading}>
              <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
              {loading ? 'Deploying…' : 'Redeploy'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 18px', overflow: 'auto', fontSize: '0.8825rem' }}>
          {/* Status line */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {loading ? (
              <Loader2 size={15} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
            ) : hasError ? (
              <AlertTriangle size={15} color="#ef4444" />
            ) : (
              <CheckCircle2 size={15} color="#22c55e" />
            )}
            <span style={{ fontWeight: 600 }}>
              {loading ? 'Deploying preview…'
                : error ? 'Deploy failed'
                : database ? 'Preview deployed'
                : 'Preview not deployed'}
            </span>
          </div>

          {error && (
            <div style={{
              marginTop: 8, padding: '8px 10px', borderRadius: 6,
              background: 'rgba(239,68,68,0.10)',
              border: '1px solid rgba(239,68,68,0.35)',
              color: '#ef4444', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {error}
            </div>
          )}

          {!loading && !error && database && (
            <dl style={{ margin: '10px 0 0', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
              <Row label="Database" value={<code style={codeStyle}>{database}</code>} />
              {schema && <Row label="Schema" value={<code style={codeStyle}>{schema}</code>} />}
              {sync && <Row label="Tables" value={summarize(sync)} />}
            </dl>
          )}

          {/* Warnings */}
          {!loading && warnings.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontWeight: 600, color: '#d97706', marginBottom: 4,
              }}>
                <AlertTriangle size={13} color="#d97706" />
                {warnings.length} deploy warning{warnings.length === 1 ? '' : 's'}
              </div>
              <ul style={{ margin: '0 0 0 20px', padding: 0, color: 'var(--color-text-secondary)' }}>
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* SQL quality findings (functions deployed, but not index-friendly) */}
          {!loading && fnWarnings.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontWeight: 600, color: '#d97706', marginBottom: 4,
              }}>
                <AlertTriangle size={13} color="#d97706" />
                {fnWarnings.length} SQL quality warning{fnWarnings.length === 1 ? '' : 's'}
              </div>
              <ul style={{ margin: '0 0 0 20px', padding: 0, color: 'var(--color-text-secondary)' }}>
                {fnWarnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* Functions */}
          {functions.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Business logic</div>
              {fnOk > 0 && (
                <div style={{ color: 'var(--color-text-secondary)', marginBottom: fnErrors.length || fnEmpty.length ? 6 : 0 }}>
                  {fnOk} function{fnOk === 1 ? '' : 's'} deployed
                </div>
              )}
              {fnErrors.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: fnEmpty.length ? 6 : 0 }}>
                  {fnErrors.map(f => (
                    <button
                      key={f.name}
                      onClick={() => { setOpen(false); onOpenFailure(f) }}
                      title={f.error_message || ''}
                      style={{
                        background: '#ef4444', color: 'white', border: 'none',
                        borderRadius: 4, padding: '4px 10px', fontSize: '0.8125rem',
                        cursor: 'pointer',
                      }}
                    >
                      {f.name} · compile error
                    </button>
                  ))}
                </div>
              )}
              {fnEmpty.length > 0 && (
                <div style={{ color: 'var(--color-text-secondary)' }}>
                  {fnEmpty.length} function{fnEmpty.length === 1 ? '' : 's'} have no body yet — open the
                  Source Editor or use AI assist.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {trigger}
      {dropdown}
      {open && createPortal(modal, document.body)}
      {resetOpen && resetConfirmName && onReset && createPortal(
        <ResetConfirmModal
          name={resetConfirmName}
          onCancel={() => setResetOpen(false)}
          onConfirm={() => { setResetOpen(false); onReset() }}
        />,
        document.body,
      )}
    </>
  )
}

function MenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 10px', fontSize: 14, textAlign: 'left',
        background: 'none', border: 'none', borderRadius: 6, cursor: 'pointer',
        color: 'var(--color-text)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-secondary, #f1f5f9)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
    >
      {children}
    </button>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt style={{ color: 'var(--color-text-secondary)' }}>{label}</dt>
      <dd style={{ margin: 0 }}>{value}</dd>
    </>
  )
}

const codeStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  background: 'var(--color-bg-secondary)',
  padding: '1px 6px', borderRadius: 3,
  wordBreak: 'break-all',
}

// Every warning surfaced by the sync report — both top-level and the
// per-table ones — flattened into a single list for display.
function collectWarnings(s: SyncReport | null): string[] {
  if (!s) return []
  const out = [...(s.warnings ?? [])]
  for (const t of s.tables ?? []) {
    for (const w of t.warnings ?? []) out.push(w)
  }
  return out
}

function summarize(s: SyncReport): string {
  const created = s.tables.filter(t => t.status === 'created').length
  const altered = s.tables.filter(t => t.status === 'altered').length
  const renamed = s.tables.filter(t => t.status === 'renamed').length
  const parts: string[] = []
  if (created) parts.push(`${created} created`)
  if (altered) parts.push(`${altered} altered`)
  if (renamed) parts.push(`${renamed} renamed`)
  return parts.length ? parts.join(', ') : 'in sync'
}
